/**
 * Laser — the timed on/off beam hazard (Brief 014). Super Meat Boy's lasers: a beam
 * that pulses lethal on a fixed cadence, so you slip through during its OFF window.
 *
 * A tile-aligned BEAM spanning `props.length` cells along `props.axis` ('x' = horizontal,
 * 'y' = vertical). An integer step counter cycles ON for `props.onSteps` then OFF for
 * `props.offSteps`; the beam is lethal ONLY while ON (instakill by default, or one heart
 * with `props.instakill:false`). The emitter shows faint while off so the threat reads.
 *
 * Determinism: a single integer step counter + modulo → ESLint-clean. (offSteps:0 = a
 * permanent beam; onSteps:0 = an inert emitter.)
 */
import type { Entity, EntityContext } from '../Entity';
import { EntityType } from '../LevelFormat';
import type { AABB } from '../TileGrid';

const DEFAULT_ON = 90;
const DEFAULT_OFF = 90;
const DEFAULT_LENGTH = 3;

export class Laser implements Entity {
  readonly type = EntityType.LASER;
  body: AABB;
  vx = 0;
  vy = 0;
  facing: 1 | -1 = 1;
  alive = true;

  private onSteps: number;
  private offSteps: number;
  private instakill: boolean;
  private tick = 0;

  /** @param props `axis` ('x'|'y'; default 'x'), `length` (cells; default 3),
   *               `onSteps` (default 90), `offSteps` (default 90),
   *               `instakill` (default true → death; false → one heart). */
  constructor(cx: number, feetY: number, props: Record<string, unknown> | undefined, ts: number) {
    const len = posInt(props?.length, DEFAULT_LENGTH);
    const axis = props?.axis === 'y' ? 'y' : 'x';
    if (axis === 'x') this.body = { x: cx - ts / 2, y: feetY - ts, w: len * ts, h: ts };
    else this.body = { x: cx - ts / 2, y: feetY - ts, w: ts, h: len * ts };
    this.onSteps = nonNeg(props?.onSteps, DEFAULT_ON);
    this.offSteps = nonNeg(props?.offSteps, DEFAULT_OFF);
    this.instakill = props?.instakill !== false; // default true
  }

  /** Is the beam lethal this step? */
  private get isOn(): boolean {
    const cycle = this.onSteps + this.offSteps;
    if (cycle <= 0) return false;
    return (this.tick % cycle) < this.onSteps;
  }

  update(_dt: number, _ctx: EntityContext): void { this.tick++; }

  onPlayerOverlap(ctx: EntityContext): void {
    if (!this.isOn) return; // safe to stand in the beam path while it's off
    // POLISH_03 #10: starBlocks:true — see Mine.ts for the rationale (the instakill:false twin
    // is already blocked by hurtPlayer's invulnSteps check).
    if (this.instakill) ctx.killPlayer(true);
    else ctx.hurtPlayer();
  }

  spriteKey(): string { return this.isOn ? 'laser_on' : 'laser_off'; }

  visible(): boolean { return true; }

  renderSize(): { w: number; h: number } { return { w: this.body.w, h: this.body.h }; }
}

function posInt(v: unknown, dflt: number): number {
  return typeof v === 'number' && v >= 1 ? Math.floor(v) : dflt;
}
function nonNeg(v: unknown, dflt: number): number {
  return typeof v === 'number' && v >= 0 ? Math.floor(v) : dflt;
}
